import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLazyQuery } from "@apollo/client";
import { Popper, Box, Stack, IconButton, Divider, Paper, List, ListItem, ListItemAvatar, ListItemText, Avatar } from '@mui/material';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import EmojiPicker from 'emoji-picker-react';
import { computePosition, flip, shift } from '@floating-ui/dom';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';

import messagingContactsLookup from "../../../graphql/queries/messagingContactsLookup";

// ---------- MentionList React component ----------
const MentionList = ({ items, command }) => {
  return (
    <Paper elevation={3}>
      <List>
        {items.map(item => (
          <ListItem button key={item.id} onClick={() => command(item)}>
            <ListItemAvatar>
              <Avatar src={item.avatar} />
            </ListItemAvatar>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

// ---------- Utility to position the dropdown ----------
const updatePosition = (editor, element, clientRect) => {
  if (!clientRect) return;

  computePosition({ getBoundingClientRect: clientRect }, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y + window.scrollY}px`;
  });
};

/**
 * MentionEditorCore
 *
 * Shared implementation behind ChatMessageInput and RichTextEditor.
 * Enter-key behavior is entirely driven by `mode`, and is handled
 * explicitly (via editor commands) instead of relying on default
 * ProseMirror keymap propagation, which is what made Shift+Enter
 * unreliable before:
 *
 *  - Shift+Enter always inserts a line break (in both modes).
 *  - mode="chat":     Enter submits (calls onEnterPressed). If the field
 *                      is uncontrolled, it then clears itself � awaiting
 *                      onEnterPressed first if it returns a promise, so a
 *                      failed send leaves the text in place. Pass
 *                      clearOnSubmit={false} to opt out.
 *  - mode="document": Enter behaves like a normal rich-text editor
 *                      (new paragraph / continues lists / etc). If
 *                      onEnterPressed is provided, Cmd/Ctrl+Enter
 *                      calls it as an explicit "submit" shortcut.
 *
 * While a @mention suggestion popup is open, Enter/Shift+Enter are
 * left alone entirely so the popup can handle selection/navigation
 * � previously Enter was intercepted at the view level (which always
 * runs before any plugin, including the mention suggestion plugin),
 * so picking a mention with Enter would incorrectly submit instead.
 */
const MentionEditorCore = forwardRef(({
  mode = 'chat', // 'chat' | 'document'
  value,
  defaultValue,
  onChange,
  onBlur,
  onEnterPressed,
  clearOnSubmit = true,
  placeholder = '',
  editable = true,
  disabled = false,
  minHeight = 150,
  disableMentions = false,
  disableToolbar = false,
}, ref) => {
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const emojiButtonRef = useRef(null);
  const mentionOpenRef = useRef(false);

  const [getContacts] = useLazyQuery(messagingContactsLookup);

  const extensions = [
    StarterKit,
    Placeholder.configure({ placeholder, emptyNodeClass: 'is-empty' }),
  ];

  if (!disableMentions) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: {
          char: '@',
          startOfLine: false,
          items: async ({ query }) => {
            if (!query || query.length < 3) return [];
            const { data } = await getContacts({ variables: { searchTerm: query } });
            if (!data) return [];
            return (data.directMessages.recipientLookup || []).map(user => ({
              id: user.userId,
              label: `${user.firstname} ${user.lastname}`,
              avatar: user.avatarUrl,
            }));
          },
          render: () => {
            let component;
            return {
              onStart: props => {
                mentionOpenRef.current = true;
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                });
                component.element.style.zIndex = 20000;
                document.body.appendChild(component.element);
                updatePosition(props.editor, component.element, props.clientRect);
              },
              onUpdate: props => {
                component.updateProps(props);
                updatePosition(props.editor, component.element, props.clientRect);
              },
              onKeyDown: props => {
                if (props.event.key === 'Escape') {
                  mentionOpenRef.current = false;
                  component.destroy();
                  return true;
                }
                return component.ref?.onKeyDown?.(props);
              },
              onExit: () => {
                mentionOpenRef.current = false;
                component.element.remove();
                component.destroy();
              },
            };
          },
        },
      })
    );
  }

  // Uncontrolled usage (defaultValue, no onChange) is supported deliberately:
  // when many of these are mounted at once (e.g. one per comment in a list),
  // wiring onChange to parent state on every keystroke gets expensive fast.
  const isControlled = value !== undefined;

  const isEditable = editable && !disabled;

  const editor = useEditor({
    extensions,
    content: isControlled ? (value || "") : (defaultValue || ""),
    editable: isEditable,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    onBlur: ({ editor }) => onBlur?.(editor.getHTML()),
    editorProps: {
      handleKeyDown(view, event) {
        if (event.key !== 'Enter') return false;

        // A mention suggestion popup is open � let it handle Enter
        // (select the highlighted item) instead of us.
        if (mentionOpenRef.current) return false;

        // Shift+Enter: always insert an explicit line break, in either mode.
        if (event.shiftKey) {
          event.preventDefault();
          editor.chain().focus().setHardBreak().run();
          return true;
        }

        if (mode === 'chat') {
          event.preventDefault();
          const html = editor.getHTML();
          const result = onEnterPressed?.(html);

          // Only auto-clear when uncontrolled � for a controlled field the
          // parent owns `value`, so clearing here would just get reverted
          // by the next sync effect. Callers that want a post-send clear
          // while controlled should reset `value` themselves.
          if (clearOnSubmit && !isControlled) {
            if (result && typeof result.then === 'function') {
              // e.g. an async "post comment" call � only clear on success,
              // so a failed send doesn't wipe out what the user typed.
              result.then(() => editor.commands.clearContent()).catch(() => { });
            } else {
              editor.commands.clearContent();
            }
          }

          return true;
        }

        // mode === 'document': plain Enter behaves normally (new paragraph,
        // continue list item, etc). Offer Cmd/Ctrl+Enter as an explicit
        // "submit" shortcut if the caller wants one.
        if ((event.metaKey || event.ctrlKey) && onEnterPressed) {
          event.preventDefault();
          onEnterPressed(editor.getHTML());
          return true;
        }

        return false;
      }
    }
  });

  useEffect(() => {
    if (editor && editor.isEditable !== isEditable) {
      editor.setEditable(isEditable);
    }
  }, [editor, isEditable]);

  useEffect(() => {
    if (isControlled && editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor, isControlled]);

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    clear: () => editor?.commands.setContent(''),
    getHTML: () => editor?.getHTML(),
    insertEmoticon: emoji => editor?.chain().focus().insertContent(emoji).run(),
  }));

  const renderButton = (icon, onClick, active, buttonRef = null) => (
    <IconButton
      size="small"
      sx={{ '& svg': { fontSize: '16px' } }}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      color={active ? 'primary' : 'default'}
      ref={buttonRef}
    >
      {icon}
    </IconButton>
  );

  if (!editor) return null;

  return (
    <Box>
      {/* Toolbar */}
      {!disableToolbar && (
        <>
          <Stack direction="row" spacing={1}>
            {renderButton(<FormatBoldIcon />, () => editor.chain().focus().toggleBold().run(), editor.isActive('bold'))}
            {renderButton(<FormatItalicIcon />, () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'))}
            {renderButton(<FormatListBulletedIcon />, () => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'))}
            {renderButton(<FormatListNumberedIcon />, () => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'))}
            {renderButton(<FormatQuoteIcon />, () => editor.chain().focus().toggleBlockquote().run(), editor.isActive('blockquote'))}
            {renderButton(
              <InsertEmoticonIcon />,
              () => setEmojiAnchor(emojiAnchor ? null : emojiButtonRef.current),
              false,
              emojiButtonRef
            )}
          </Stack>
          <Divider sx={{ mt: 1 }} />
        </>
      )}

      {/* Editor */}
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          minHeight,
          '& .ProseMirror': {
            outline: 'none',
            padding: 1,
            minHeight,
            cursor: editable ? 'text' : 'default',
            position: 'relative',
            '& p:first-of-type': { marginTop: 0 },
            '& p.is-empty:first-child::before': {
              content: 'attr(data-placeholder)',
              color: '#888',
              fontStyle: 'italic',
              position: 'absolute',
              left: 8,
              top: 8,
              pointerEvents: 'none',
              whiteSpace: 'pre-wrap',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Emoji picker */}
      {!disableToolbar && (
        <Popper open={Boolean(emojiAnchor)} anchorEl={emojiAnchor} placement="bottom-start" style={{ zIndex: 2000 }}>
          <EmojiPicker
            onEmojiClick={emojiObject => {
              editor.chain().focus().insertContent(emojiObject.emoji).run();
              setEmojiAnchor(null);
            }}
            searchDisabled
            previewConfig={{ showPreview: false }}
            emojiStyle="native"
            emojiVersion="4.0"
            height={300}
            width={300}
          />
        </Popper>
      )}
    </Box>
  );
});

MentionEditorCore.displayName = 'MentionEditorCore';

/**
 * Comment/chat-style input: Enter sends, Shift+Enter inserts a line break.
 * Pass `value`/`onChange` for a controlled field, or `defaultValue` alone
 * for an uncontrolled one (cheaper when many are mounted at once, e.g.
 * one per row in a comment list) � use the ref's `getHTML()`/`clear()`
 * in that case instead of parent state.
 */
export const ChatMessageInput = forwardRef((props, ref) => (
  <MentionEditorCore {...props} mode="chat" ref={ref} />
));
ChatMessageInput.displayName = 'ChatMessageInput';

/**
 * Rich-text/document-style editor: Enter inserts a newline (new paragraph,
 * continues lists, etc), Shift+Enter inserts a plain line break. Pass
 * onEnterPressed if you want Cmd/Ctrl+Enter to act as a "submit" shortcut.
 */
export const RichTextEditor = forwardRef((props, ref) => (
  <MentionEditorCore {...props} mode="document" ref={ref} />
));
RichTextEditor.displayName = 'RichTextEditor';

export default MentionEditorCore;